import { FC, Fragment, useMemo, useState } from 'react';

import AutTable from '../../../blocks/aut-grid-table';
import AnomalyItemPDOForm, { AnomalyItemWithPDO as Anomaly } from '../../../forms/custom-fvi/anomalyWithPDO';

import { getNewCustomFVITableColumns } from '../../../../utils/data';
import { Column, DamageClassItem } from '../../../../utils/models';
import { matchPhrases } from '../../../../utils/utilities';

interface Props {
    nextAnomalyID: number;
    damageClasses: DamageClassItem[];
    anomalyItems: Anomaly[];
    onDataChange: (items: Anomaly[]) => void;
}

const PDOPipingAnomalyItems: FC<Props> = (props) => {
    const [itemToEdit, setItemToEdit] = useState<Anomaly | null>(null);

    const getNewAnomalyID = () => {
        if (props.anomalyItems.length === 0) return props.nextAnomalyID;

        return props.anomalyItems[props.anomalyItems.length - 1].id + 1;
    };

    const handleAddItem = (item: Anomaly) => {
        const newAnomalyItems = [...props.anomalyItems];
        newAnomalyItems.push({ ...item, id: getNewAnomalyID() });

        props.onDataChange(newAnomalyItems);
    };

    const handleDeleteItem = (item: Anomaly): void => {
        const newAnomalyItems = props.anomalyItems.filter((i) => i.id !== item.id);
        props.onDataChange(newAnomalyItems);
    };

    const handleEditItem = (item: Anomaly): void => {
        props.onDataChange(props.anomalyItems.map((anomaly) => anomaly.id === item.id ? item : anomaly));
        setItemToEdit(null);
    };

    const handleCancelEdit = (): void => {
        setItemToEdit(null);
    };

    const columns: Column[] = useMemo(() => {
        return getNewCustomFVITableColumns().concat(pdoColumns);
    }, []);

    return (
        <Fragment>
            {itemToEdit ? (
                <AnomalyItemPDOForm
                    anomalyItem={itemToEdit}
                    damageClasses={props.damageClasses} 
                    onAdd={handleEditItem} 
                    onCancelEdit={handleCancelEdit}
                />
            ) : (
                <AnomalyItemPDOForm
                    damageClasses={props.damageClasses} 
                    onAdd={handleAddItem} 
                />
            )}

            <div className="divider my-4"></div>

            <AutTable
                data={props.anomalyItems}
                tableColumns={columns}
                onDeleteItem={handleDeleteItem}
                onEditItem={(item: Anomaly) => setItemToEdit(item)}
            />
        </Fragment>
    );
};

const pdoColumns: Column[] = [
    {
        path: 'pdoDescription',
        label: 'PDO Description',
        content: (item) => matchPhrases(item.hasPDO, 'yes') ? item.pdoDescription : 'N/A',
        headerStyle: { padding: '0.25rem', fontSize: '0.675rem', textTransform: 'uppercase' }
    },
    {
        path: 'pdoLocation',
        label: 'PDO Location',
        content: (item) => matchPhrases(item.hasPDO, 'yes') ? item.pdoLocation : 'N/A',
        headerStyle: { padding: '0.25rem', fontSize: '0.675rem', textTransform: 'uppercase' }
    },
    {
        path: 'height',
        label: 'Height (M) (Approx.)',
        content: (item) => matchPhrases(item.hasPDO, 'yes') ? item.height : 'N/A',
        headerStyle: { padding: '0.25rem', fontSize: '0.675rem', textTransform: 'uppercase' }
    },
    {
        path: 'weight',
        label: 'Weight (KG) (Approx.)',
        content: (item) => matchPhrases(item.hasPDO, 'yes') ? item.weight : 'N/A',
        headerStyle: { padding: '0.25rem', fontSize: '0.675rem', textTransform: 'uppercase' }
    },
    {
        path: 'threat',
        label: 'Threat To',
        content: (item) => matchPhrases(item.hasPDO, 'yes') ? item.threat : 'N/A',
        headerStyle: { padding: '0.25rem', fontSize: '0.675rem', textTransform: 'uppercase' }
    },
];

export { pdoColumns };
export default PDOPipingAnomalyItems;
